import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase.js';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import Select from '../common/Select';

const CreateIssue = ({ onClose, onIssueCreated }) => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    type: 'task',
    priority: 'medium',
    assigneeId: '',
    storyPoints: ''
  });

  const issueTypes = [
    { value: 'story', label: 'Story' },
    { value: 'task', label: 'Task' },
    { value: 'bug', label: 'Bug' },
    { value: 'epic', label: 'Epic' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  useEffect(() => {
    const fetchProjects = async () => {
      if (!userProfile) return;

      try {
        const projectsQuery = query(
          collection(db, 'projects'),
          where('members', 'array-contains', userProfile.uid)
        );
        const snapshot = await getDocs(projectsQuery);
        const projectsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjects(projectsData);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, [userProfile]);

  useEffect(() => {
    const fetchProjectMembers = async () => {
      if (!formData.projectId) {
        setProjectMembers([]);
        return;
      }

      try {
        const projectDoc = await getDoc(doc(db, 'projects', formData.projectId));
        if (!projectDoc.exists()) {
          console.error('Project not found:', formData.projectId);
          return;
        }

        const project = projectDoc.data();

        if (!project.members || project.members.length === 0) {
          setProjectMembers([]);
          return;
        }

        const membersQuery = query(
          collection(db, 'users'),
          where('uid', 'in', project.members)
        );
        const membersSnapshot = await getDocs(membersQuery);
        const membersData = membersSnapshot.docs.map(doc => ({
          id: doc.data().uid,
          ...doc.data()
        }));

        setProjectMembers(membersData);

      } catch (error) {
        console.error('Error fetching project members:', error);
        await fetchMembersSequentially(formData.projectId);
      }
    };

    const fetchMembersSequentially = async (projectId) => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (!projectDoc.exists()) return;

        const project = projectDoc.data();
        const membersData = [];

        for (const memberId of project.members || []) {
          try {
            const userDoc = await getDoc(doc(db, 'users', memberId));
            if (userDoc.exists()) {
              membersData.push({
                id: userDoc.data().uid,
                ...userDoc.data()
              });
            }
          } catch (error) {
            console.error(`Error fetching user ${memberId}:`, error);
          }
        }

        setProjectMembers(membersData);
      } catch (error) {
        console.error('Error in sequential member fetch:', error);
      }
    };

    fetchProjectMembers();
  }, [formData.projectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'projectId') {
      setFormData(prev => ({
        ...prev,
        projectId: value,
        assigneeId: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.projectId) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      const project = projects.find(p => p.id === formData.projectId);
      if (!project) throw new Error('Project not found');

      const issuesQuery = query(
        collection(db, 'issues'),
        where('projectId', '==', formData.projectId)
      );
      const issuesSnapshot = await getDocs(issuesQuery);
      const issueNumber = issuesSnapshot.size + 1;
      const issueKey = `${project.key}-${issueNumber}`;

      const assignee = projectMembers.find(m => m.id === formData.assigneeId);
      const assigneeName = assignee?.displayName || '';

      const issueData = {
        ...formData,
        issueKey,
        status: 'todo',
        createdBy: userProfile.uid,
        creatorName: userProfile.displayName,
        assigneeName: assigneeName,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'issues'), issueData);
      
      onIssueCreated({
        id: docRef.id,
        ...issueData
      });
    } catch (error) {
      console.error('Error creating issue:', error);
      alert('Failed to create issue: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Create New Issue"
      onClose={onClose}
      size="large"
    >
      <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
        <Input
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Enter issue title"
          required
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter issue description"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 resize-none text-gray-900 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
          <Select
            label="Project"
            name="projectId"
            value={formData.projectId}
            onChange={handleChange}
            options={[
              { value: '', label: 'Select a project' },
              ...projects.map(project => ({
                value: project.id,
                label: `${project.emoji} ${project.name}`
              }))
            ]}
            required
          />
          
          <Select
            label="Issue Type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            options={issueTypes}
            required
          />
          
          <Select
            label="Priority"
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            options={priorityOptions}
            required
          />
          
          <Select
            label="Assignee"
            name="assigneeId"
            value={formData.assigneeId}
            onChange={handleChange}
            options={[
              { value: '', label: 'Unassigned' },
              ...projectMembers.map(member => ({
                value: member.id,
                label: member.displayName || member.email
              }))
            ]}
            disabled={!formData.projectId || projectMembers.length === 0}
          />
        </div>

        <Input
          label="Story Points"
          name="storyPoints"
          type="number"
          value={formData.storyPoints}
          onChange={handleChange}
          placeholder="Estimate story points"
          min="0"
          max="100"
        />

        <div className="flex flex-col-reverse sm:flex-row justify-end space-y-3 sm:space-y-0 space-x-0 sm:space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="w-full sm:w-auto min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="w-full sm:w-auto min-w-[100px]"
          >
            Create Issue
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateIssue;